import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  contentChildren,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

let nextNavId = 0;

@Component({
  selector: 'fc-tab-nav-panel',
  template: `<ng-content></ng-content>`,
  host: {
    role: 'tabpanel',
    '[attr.id]': 'id()',
    '[attr.aria-labelledby]': 'activeTabId()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FcTabNavPanel {
  readonly id = input(`fc-tab-nav-panel-${nextNavId++}`);
  readonly activeTabId = signal<string | undefined>(undefined);
}

@Component({
  selector: 'a[fc-tab-link], button[fc-tab-link]',
  template: `<ng-content></ng-content>`,
  styles: `
    :host {
      position: relative;
      display: inline-flex;
      flex: 0 0 auto;
      min-width: 52px;
      height: 40px;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border: 0;
      border-bottom: 2px solid transparent;
      background: transparent;
      color: var(--color-secondary-400);
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0;
      padding: 0 16px;
      text-decoration: none;
      white-space: nowrap;
      transition:
        background-color 120ms ease,
        border-color 120ms ease,
        color 120ms ease;
    }

    :host(:hover:not(.fc-tab-link-disabled)) {
      background: var(--color-secondary-50);
    }

    :host(:active:not(.fc-tab-link-disabled)) {
      background: var(--color-secondary-100);
    }

    :host(:focus-visible) {
      outline: 2px solid var(--color-primary-300);
      outline-offset: -2px;
    }

    :host(.fc-tab-link-active) {
      border-bottom-color: var(--color-primary-600);
      color: var(--color-primary-600);
    }

    :host(.fc-tab-link-disabled) {
      border-bottom-color: transparent;
      color: var(--color-secondary-300);
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  `,
  host: {
    class: 'fc-tab-link',
    '[class.fc-tab-link-active]': 'active()',
    '[class.fc-tab-link-disabled]': 'disabled()',
    '[attr.id]': 'id()',
    '[attr.role]': 'nav?.tabPanel() ? "tab" : null',
    '[attr.aria-controls]': 'nav?.tabPanel()?.id() ?? null',
    '[attr.aria-current]': '!nav?.tabPanel() && active() ? "page" : null',
    '[attr.aria-selected]': 'nav?.tabPanel() ? active() : null',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '[attr.tabindex]': 'disabled() ? -1 : tabIndex()',
    '(click)': 'onClick($event)',
    '(focus)': 'scrollIntoView()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FcTabLink {
  readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly nav = inject(FcTabNav, { optional: true, host: true });

  readonly active = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly tabIndex = input(0);
  readonly id = input(`fc-tab-link-${nextNavId++}`);

  constructor() {
    effect(() => {
      // Re-evaluate the nav's active link whenever this link's state changes.
      this.active();
      this.disabled();
      this.nav?.updateActiveLink();
    });
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  scrollIntoView(): void {
    this.elementRef.nativeElement.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    });
  }

  onClick(event: MouseEvent): void {
    if (!this.disabled()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

@Component({
  selector: 'nav[fc-tab-nav-bar]',
  template: `
    <div class="fc-tab-nav-scroll min-w-0 overflow-x-auto overflow-y-hidden">
      <div class="flex min-w-0" [class.fc-tab-nav-list-stretch]="stretchTabs()">
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      min-width: 0;
      border-bottom: 1px solid var(--color-grey-100);
    }

    .fc-tab-nav-scroll {
      scrollbar-width: none;
    }

    .fc-tab-nav-scroll::-webkit-scrollbar {
      display: none;
    }

    .fc-tab-nav-list-stretch ::ng-deep .fc-tab-link {
      flex: 1 0 auto;
    }
  `,
  host: {
    '[attr.role]': 'tabPanel() ? "tablist" : "navigation"',
    '(keydown)': 'onKeydown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FcTabNav {
  readonly stretchTabs = input(false, {
    alias: 'fc-stretch-tabs',
    transform: booleanAttribute,
  });
  readonly animationDuration = input<string | number | null>(null);
  readonly color = input<string | null>(null);
  readonly tabPanel = input<FcTabNavPanel>();

  private readonly links = contentChildren(FcTabLink, { descendants: true });

  constructor() {
    effect(() => {
      // React to links being added or removed.
      this.links();
      this.updateActiveLink();
    });
  }

  updateActiveLink(): void {
    queueMicrotask(() => {
      const activeLink = this.links().find((link) => link.active());
      this.tabPanel()?.activeTabId.set(activeLink?.id());
      activeLink?.scrollIntoView();
    });
  }

  onKeydown(event: KeyboardEvent): void {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const enabledLinks = this.links().filter((link) => !link.disabled());
    if (!enabledLinks.length) return;

    const activeElement = document.activeElement;
    const currentIndex = enabledLinks.findIndex(
      (link) =>
        link.elementRef.nativeElement === activeElement || link.active(),
    );
    const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
    let nextIndex = safeCurrentIndex;

    if (event.key === 'ArrowLeft') {
      nextIndex = Math.max(0, safeCurrentIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      nextIndex = Math.min(enabledLinks.length - 1, safeCurrentIndex + 1);
    }
    if (event.key === 'Home') {
      nextIndex = 0;
    }
    if (event.key === 'End') {
      nextIndex = enabledLinks.length - 1;
    }

    event.preventDefault();
    enabledLinks[nextIndex]?.focus();
  }
}
